import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import CN19FileStatusTable from './CN19FileStatusTable';
import CN19ItemsWeightsTable from './CN19ItemsWeightsTable';
import CN19ChargesTable from './CN19ChargesTable';

interface QuarterlyData {
  sendingDo: string;
  receivingDo: string;
  accountingQuarter: string;
  normalWeightA: string;
  normalWeightD: string;
  normalTotalWeight: string;
  normalItemsA: string;
  normalItemsD: string;
  normalTotalItems: string;
  bulkyWeightA: string;
  bulkyWeightD: string;
  bulkyTotalWeight: string;
  bulkyItemsA: string;
  bulkyItemsD: string;
  bulkyTotalItems: string;
}

interface TotalData {
  normalWeightA: string;
  normalWeightD: string;
  normalTotalWeight: string;
  normalItemsA: string;
  normalItemsD: string;
  normalTotalItems: string;
  bulkyWeightA: string;
  bulkyWeightD: string;
  bulkyTotalWeight: string;
  bulkyItemsA: string;
  bulkyItemsD: string;
  bulkyTotalItems: string;
}

interface IbrsItemAndWeight {
  quarterOneIbrsItemAndWeight: QuarterlyData;
  quarterTwoIbrsItemAndWeight: QuarterlyData;
  quarterThreeIbrsItemAndWeight: QuarterlyData;
  quarterFourIbrsItemAndWeight: QuarterlyData;
  totalIbrsItemAndWeightItem: TotalData;
}

interface ChargeDetail {
  cn10YearlyTotal: string;
  rate: string;
  total: string;
}

interface IbrsCharge {
  sendingDo: string;
  receivingDo: string;
  normalWeight: ChargeDetail;
  normalItems: ChargeDetail;
  bulkyWeight: ChargeDetail;
  bulkyItems: ChargeDetail;
  total: string;
}

interface FileStatus {
  filename: string;
  status: string;
}

interface CN19Data {
  designatedOperator: string;
  loggedInDesignatedOperator: string;
  accountingYear: string;
  currency: string;
  inboundE09FileNames: FileStatus[];
  outboundE09FileNames: FileStatus[];
  inboundIbrsItemAndWeight: IbrsItemAndWeight;
  outboundIbrsItemAndWeight: IbrsItemAndWeight;
  inboundIbrsCharge: IbrsCharge;
  outboundIbrsCharge: IbrsCharge;
  creditor: string;
  ibrsChargeTotal: string;
}

interface CN19TableProps {
  data: CN19Data[];
}

function CN19Table({ data }: CN19TableProps): React.ReactElement {
  const cn19Data = data[0]; // Assuming single record for now

  if (!cn19Data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn19-table-empty">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn19-table-empty-text">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="cn19-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 4 }} data-testid="cn19-table-header">
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142', mb: 1 }} data-testid="cn19-table-title">
          CN19 IBRS Report - {cn19Data.designatedOperator}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142', mb: 2 }} data-testid="cn19-table-year">
          Accounting Year: {cn19Data.accountingYear}
        </Typography>
      </Box>

      {/* File Status Tables */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 4 
        }}
        data-testid="cn19-table-file-status-row"
      >
        <CN19FileStatusTable 
          title="Inbound" 
          fileNames={cn19Data.inboundE09FileNames}
          data-testid="cn19-table-inbound-files"
        />
        
        <CN19FileStatusTable 
          title="Outbound" 
          fileNames={cn19Data.outboundE09FileNames}
          data-testid="cn19-table-outbound-files"
        />
      </Box>

      {/* IBRS Items and Weights Tables */}
      <CN19ItemsWeightsTable 
        title="1. IBRS Items and Weights from CN10  " 
        data={cn19Data.inboundIbrsItemAndWeight}
        type="inbound"
        data-testid="cn19-table-inbound-items-weights"
      />

      <CN19ItemsWeightsTable 
        data={cn19Data.outboundIbrsItemAndWeight}
        type="outbound"
        data-testid="cn19-table-outbound-items-weights"
      />

      {/* IBRS Charges Table */}
      <CN19ChargesTable 
        title="2. IBRS Charges"
        inboundCharge={cn19Data.inboundIbrsCharge}
        outboundCharge={cn19Data.outboundIbrsCharge}
        totalCharge={cn19Data.ibrsChargeTotal}
        currency={cn19Data.currency}
        creditor={cn19Data.creditor}
        data-testid="cn19-table-charges"
      />
    </Box>
  );
}

export default CN19Table; 